import { faXmark, faCheck, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { forwardRef, useEffect, useState } from 'react'
import { useOnClickOutside } from 'usehooks-ts'

import { useTranslation } from 'services/i18n'
import { cn } from 'utils/cn'

import type { ChangeEvent, KeyboardEvent, RefObject } from 'react'

type Props = {
  initialText: string
  isConversationSubjectUpdating: boolean
  onSubmit: (text: string) => void
  onCancel: VoidFunction
  onComponentMount?: VoidFunction
}

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      initialText,
      isConversationSubjectUpdating,
      onSubmit,
      onCancel,
      onComponentMount,
    },
    ref,
  ) => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'conversation_history',
    })

    const [text, setText] = useState(initialText)
    const validInput = text.trim() && text.trim() !== initialText

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      setText(e.target.value)
    }

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation()

      if (e.key === 'Enter' && validInput) {
        onSubmit(text)
      }
      else if (e.key === 'Escape' || (e.key === 'Enter' && !validInput)) {
        onCancel()
      }
    }

    useOnClickOutside(ref as RefObject<HTMLInputElement>, () => {
      if (validInput) onSubmit(text)
      else onCancel()
    })

    useEffect(() => {
      onComponentMount?.()
    }, [onComponentMount])

    return (
      <span className="relative w-full">
        <input
          className="w-full rounded bg-default px-[6px] py-1 pr-8 text-default ring-grey-100 focus:outline-none"
          disabled={isConversationSubjectUpdating}
          onChange={onChange}
          onKeyDown={onKeyDown}
          ref={ref}
          value={text}
        />
        <FontAwesomeIcon
          icon={faSpinnerThird}
          className={cn(
            'animate-spin absolute right-0 top-1.5 mr-1 opacity-0 transition duration-150',
            { 'opacity-100': isConversationSubjectUpdating },
          )}
        />
        <button
          type="button"
          onClick={validInput ? () => onSubmit(text) : onCancel}
          className={cn(
            'absolute right-0 top-[1px] mr-2 h-full opacity-0 hover:bg-subdued lg:focus:bg-dark',
            {
              'opacity-100 cursor-pointer': !isConversationSubjectUpdating,
            },
          )}
          aria-label={validInput ? t('edit_conversation_subject_submit') : t('edit_conversation_subject_cancel')}
          disabled={isConversationSubjectUpdating}
        >
          <FontAwesomeIcon icon={validInput ? faCheck : faXmark} className="text-sm" />
        </button>
      </span>
    )
  },
)

Input.displayName = 'Input'
